import { useCallback, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { updateCanvasStoryboards } from "@/store/supabaseSlice";
import { useInitializeStoryboard } from "@/hooks/storyboards/useInitializeStoryboards";
import { useActiveCanvas, useActiveProject, useCanvasId } from "@/utils/utils";
import { CursorType, setCursorType } from "@/store/globalSettingsSlice";
import { LayoutId } from "@/components/AppHeader/hooks/useLayoutPresets";
import {
  createDummyComponent,
  getInstantUpdateHtmlForStoryboard,
} from "@/components/AppHeader/utils";
import { useCreateStoryboard } from "@/hooks/storyboards/useCreateStoryboard";
import type { Storyboard as StoryboardType } from "@/utils/types";
import { useIframePoolStore } from "@/components/Canvas/IframePool/iframePoolStore";
import { processInstantUpdateData } from "@/utils/navTreeUtils";
import { updateAllStoreLookups } from "@/components/Canvas/Storyboard/StaticIframe/utils/navTreeLookupUtils";
import { toast } from "sonner";
import { useLayoutAndTypography } from "@/components/AppHeader/hooks/useLayoutAndTypography";
import {
  CanvasItem,
  TEMP_ELEMENT_DRAG_STORYBOARD_ID,
} from "@/components/SimpleCanvas/SimpleCanvas";
import { StoryboardDelta } from "@/store/zustand/changeLedgerTypes";
import { usePagesStore } from "@/store/zustand/pagesStore";

export const useCreateAndUpdateStoryboards = () => {
  const dispatch = useDispatch();
  const canvasData = useActiveCanvas();
  const project = useActiveProject();
  const initializeStoryboardHook = useInitializeStoryboard();
  const cursorType = useSelector(
    (state: any) => state.globalSettings.cursorType,
  );
  const cursorTypeRef = useRef(cursorType);
  useEffect(() => {
    cursorTypeRef.current = cursorType;
  }, [cursorType]);
  const isCursorText = cursorType === CursorType.Text;

  const {
    selectedLayoutOptionToUse,
    selectedTypographyOptionToUse,
    usingTailwind,
  } = useLayoutAndTypography();

  const canvasId = useCanvasId();

  const createStoryboard = useCreateStoryboard();

  // Get the currently selected page ID
  const selectedPageId = usePagesStore(
    (state) => state.selectedPageId[canvasId],
  );

  const sbValuesAtStartOfAction = useRef<
    Record<string, Partial<StoryboardType>>
  >({});

  // Update storyboards function (from CanvasV2.tsx onItemsChange)
  const updateStoryboards = useCallback(
    (items: CanvasItem[], endOfAction: boolean) => {
      if (canvasData) {
        const sbIdToCurrentValue = new Map<string, StoryboardType>();
        canvasData.storyboards.forEach((sb) => {
          sbIdToCurrentValue.set(sb.id, sb);
        });

        // Get page info for deltas
        const pageConfigPath = selectedPageId
          ? usePagesStore
              .getState()
              .getPageSourcePath(canvasData.id, selectedPageId)
          : undefined;

        const storyboardDeltas = items
          .filter(
            (item) => item.id && item.id !== TEMP_ELEMENT_DRAG_STORYBOARD_ID,
          )
          .map((item) => {
            // Return only the changed fields
            const changedFields = Object.keys(item).reduce((acc, key) => {
              if (item[key] !== sbIdToCurrentValue.get(item.id)?.[key]) {
                acc[key] = item[key];
              }

              if (!endOfAction) {
                if (!sbValuesAtStartOfAction.current[item.id]) {
                  sbValuesAtStartOfAction.current[item.id] = {};
                }

                if (
                  sbValuesAtStartOfAction.current[item.id][key] === undefined
                ) {
                  sbValuesAtStartOfAction.current[item.id][key] =
                    sbIdToCurrentValue.get(item.id)?.[key];
                }
              }

              return acc;
            }, {});
            return {
              changeType: "update" as const,
              storyboardId: item.id,
              pageId: selectedPageId || "",
              pageConfigPath,
              storyboardUpdateDict: changedFields,
            };
          })
          .filter(
            (item) =>
              Object.keys(item.storyboardUpdateDict).length > 0 &&
              item.storyboardId,
          ) as StoryboardDelta[];

        if (endOfAction) {
          updateCanvasStoryboards(
            canvasData.id,
            storyboardDeltas,
            sbValuesAtStartOfAction.current,
            true,
          );
          sbValuesAtStartOfAction.current = {};
        }
      }
    },
    [canvasData, selectedPageId],
  );

  // Initialize storyboard function (from CanvasV2.tsx)
  const startDrawStoryboard = useCallback(
    (x: number, y: number, width: number = 1, height: number = 1) => {
      const drawingApplicationStoryboard =
        cursorTypeRef.current === CursorType.Draw &&
        selectedLayoutOptionToUse.id === LayoutId.APPLICATION;

      const storyboard = initializeStoryboardHook(
        x,
        y,
        width,
        height,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        drawingApplicationStoryboard,
      );

      // Also create the instant update HTML and set it for the storyboard
      const { instantUpdateHtml } = getInstantUpdateHtmlForStoryboard(
        isCursorText,
        usingTailwind,
        selectedTypographyOptionToUse,
        selectedLayoutOptionToUse,
        project,
        "FULL",
        "FULL",
      );

      const {
        html: htmlForInstantUpdate,
        navTree,
        error,
      } = processInstantUpdateData({
        storyboardId: storyboard.id,
        canvasId,
        projectSubfolderPath: project?.subfolderPath,
        instantUpdateData: {
          html: instantUpdateHtml,
        },
        wrapInShell: true,
      });

      if (error) {
        toast.error(error);
      }

      if (htmlForInstantUpdate) {
        useIframePoolStore
          .getState()
          .setHtmlForStoryboard(storyboard.id, htmlForInstantUpdate);
      }

      if (navTree) {
        updateAllStoreLookups(storyboard.id, { navTree }, true);
      }

      return storyboard;
    },
    [
      initializeStoryboardHook,
      isCursorText,
      usingTailwind,
      selectedTypographyOptionToUse,
      selectedLayoutOptionToUse,
      project,
      canvasId,
    ],
  );

  // Finalize storyboard function (from Canvas.tsx logic after setModal)
  const finalizeDrawStoryboard = useCallback(
    async (storyboard: StoryboardType) => {
      if (!canvasData) {
        console.error("Canvas data not found");
        return;
      }

      // Reset cursor type
      dispatch(setCursorType(CursorType.Pointer));

      const width = Math.round(storyboard.width);
      const height = Math.round(storyboard.height);

      // Handle application storyboard case
      if (selectedLayoutOptionToUse.id === LayoutId.APPLICATION) {
        if (!selectedPageId) {
          console.error(
            "[finalizeDrawStoryboard] No page selected - cannot create application storyboard",
          );
          toast.error("Please select a page before drawing a storyboard");
          return;
        }

        const newApplicationStoryboard = {
          ...initializeStoryboardHook(
            storyboard.x,
            storyboard.y,
            width,
            height,
            storyboard.id,
            undefined,
            undefined,
            false,
            undefined,
            true,
          ),
          pageId: selectedPageId,
          route: "/", // Default route for new application storyboards
          created: true,
          created_at: new Date().toISOString(),
        };

        const pageConfigPath = usePagesStore
          .getState()
          .getPageSourcePath(canvasId, selectedPageId);

        const storyboardDeltas: StoryboardDelta[] = [
          {
            changeType: "upsert",
            pageId: selectedPageId,
            pageConfigPath,
            storyboardId: newApplicationStoryboard.id,
            storyboardUpdateDict: newApplicationStoryboard,
          },
        ];
        await updateCanvasStoryboards(
          canvasData.id,
          storyboardDeltas,
          undefined,
          true,
        );
        return;
      }

      const { instantUpdateHtml, jsxContents } =
        getInstantUpdateHtmlForStoryboard(
          isCursorText,
          usingTailwind,
          selectedTypographyOptionToUse,
          selectedLayoutOptionToUse,
          project,
          width,
          height,
        );

      // Create the finalized storyboard
      const newStoryboard = initializeStoryboardHook(
        storyboard.x,
        storyboard.y,
        width,
        height,
        storyboard.id,
      );

      const dummyComponentContents = createDummyComponent(
        jsxContents,
        project.framework,
        newStoryboard.name,
      );

      if (!selectedPageId) {
        console.error(
          "[finalizeDrawStoryboard] No page selected - cannot create storyboard",
        );
        toast.error("Please select a page before drawing a storyboard");
        return;
      }

      await createStoryboard({
        storyboard: newStoryboard,
        pageId: selectedPageId,
        fileContentsToCreateWith: dummyComponentContents,
        actionText: "Drawn Component",
        instantUpdateData: {
          html: instantUpdateHtml,
        },
        editTextAfterCreate: isCursorText,
      });
    },
    [
      selectedLayoutOptionToUse,
      selectedTypographyOptionToUse,
      usingTailwind,
      isCursorText,
      canvasData,
      initializeStoryboardHook,
      project,
      createStoryboard,
      dispatch,
      selectedPageId,
    ],
  );

  return {
    updateStoryboards,
    startDrawStoryboard,
    finalizeDrawStoryboard,
  };
};
